import { useMemo } from 'react';

import { Group, Progress, ProgressRootProps, Stack } from 'ui';

import { centsToDollar } from '../../utils';
import { AmountOverline } from '../Text';

interface ProgressDataProps extends Partial<ProgressRootProps> {
  amount_raised: number;
  goal?: number;
  size?: 'lg' | 'sm';
  amount_pledged?: number;
  removeTrailingCents?: boolean;
  showAmountRaised?: boolean;
}

const ProgressData = ({
  amount_raised,
  goal,
  size,
  amount_pledged,
  removeTrailingCents = false,
  showAmountRaised = true,
  ...rest
}: ProgressDataProps) => {
  const percent =
    amount_raised !== undefined && goal ? Math.round((amount_raised / goal) * 100) : undefined;

  const amountPledgedString = `${centsToDollar(amount_pledged)} pledged`;

  const percentString = percent ? `${percent}%` : undefined;

  const partialAmountString = amount_pledged ? amountPledgedString : percentString;

  const pledgedPercent = amount_pledged && goal && Math.round((amount_pledged / goal) * 100);

  const progressSections = useMemo(() => {
    if (!percent) return [];
    if (pledgedPercent) {
      return [
        { value: percent, color: 'mint' },
        { value: pledgedPercent, color: 'cobalt.3' },
      ];
    } else {
      return [{ value: percent, color: 'mint' }];
    }
  }, [percent, pledgedPercent]);

  return (
    <Stack gap="sm" flex={1}>
      <Group justify={'space-between'}>
        {showAmountRaised && (
          <AmountOverline
            title={'Raised So Far'}
            amount={amount_raised}
            size={size}
            partial_amount={partialAmountString}
            removeTrailingCents={removeTrailingCents}
          />
        )}
        {goal ? (
          <AmountOverline
            title={'Goal'}
            amount={goal}
            size={size}
            removeTrailingCents={removeTrailingCents}
          />
        ) : null}
      </Group>
      {showAmountRaised && Boolean(progressSections?.length > 0) && (
        <Progress.Root {...rest}>
          {progressSections?.map((section, index) => (
            <Progress.Section key={index} value={section.value} color={section.color} />
          ))}
        </Progress.Root>
      )}
    </Stack>
  );
};

export { ProgressData };
